import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faArrowsToCircle,
	faChartLine,
	faEye,
	faEyeSlash,
	faFileContract,
	faGlobe,
	faMoon,
	faSun,
	faThumbsUp,
	faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LicenseInfo } from '@mui/x-license-pro';
import { lazy } from 'react';
import Layout from '~/components/Layout';
import type { PreRoute } from '~/models/route';
import CategoriesIndividual from '~/pages/categoriesIndividual';
import EditUser from '~/pages/EditUser';
import NoPermissionsPage from '~/pages/no_permissions';
import Relativity from '~/pages/relativity';

const Home = lazy(() => import('../pages/index'));
const Categories = lazy(() => import('../pages/categories'));
const Policies = lazy(() => import('../pages/policies'));
const ReferringDomain = lazy(() => import('../pages/referringDomain'));
const DiscountCodes = lazy(() => import('../pages/discountCodes'));
const JourneySystem = lazy(() => import('../pages/journeySystem'));
const Profile = lazy(() => import('../pages/profile'));
const Users = lazy(() => import('../pages/users'));
const GallagherUsers = lazy(() => import('../pages/gallagher_users'));
const Offline = lazy(() => import('../pages/offline'));
const Private = lazy(() => import('../components/Private'));
const Login = lazy(() => import('../pages/login'));
const Register = lazy(() => import('../pages/register'));
const Logout = lazy(() => import('../pages/logout'));
const ChangeEmail = lazy(() => import('../pages/change_email'));
const AKMarketing = lazy(() => import('../pages/ak_marketing'));
const BundleAnalytics = lazy(() => import('../pages/bundleAnalytics'));
const BasketAnalytics = lazy(() => import('../pages/basketAnalytics'));
const VerifyEmail = lazy(() => import('../pages/verify_email'));
const ResetPassword = lazy(() => import('../pages/reset_password'));
const ConfirmPassword = lazy(() => import('../pages/confirm_password'));
const Concierge = lazy(() => import('../pages/concierge'));
const GISSystem = lazy(() => import('../pages/gisSystem'));
const UniPolicies = lazy(() => import('../pages/uniPolicies'));
const UniPolicyDetails = lazy(() => import('../pages/uniPolicyDetails'));
const FeefoSales = lazy(() => import('../pages/feefoSales'));
const SystemAdmin = lazy(() => import('../pages/systemAdmin'));

library.add(
	faTrash,
	faEye,
	faEyeSlash,
	faArrowsToCircle,
	faSun,
	faMoon,
	faChartLine,
	faGlobe,
	faFileContract,
	faThumbsUp
);

type RuntimeEnv = {
	MUI_LICENSE_KEY?: string;
};

const runtimeMuiKey =
	typeof window !== 'undefined'
		? (window as unknown as { __ENV__?: RuntimeEnv }).__ENV__?.MUI_LICENSE_KEY ??
		  (window as unknown as { __MUI_LICENSE_KEY__?: string }).__MUI_LICENSE_KEY__
		: undefined;

LicenseInfo.setLicenseKey(runtimeMuiKey ?? process.env.MUI_LICENSE_KEY ?? '');

const routes: PreRoute[] = [
	{
		path: '/login',
		element: <Login />,
	},
	{
		path: '/no-permissions',
		element: <NoPermissionsPage />,
	},
	{
		path: '/logout',
		element: <Logout />,
	},
	{
		path: '/register',
		element: <Register />,
	},
	{
		path: '/change-email',
		element: <ChangeEmail />,
	},
	{
		path: '/verify-email',
		element: <VerifyEmail />,
	},
	{
		path: '/reset-password',
		element: <ResetPassword />,
	},
	{
		path: '/concierge',
		element: <Concierge />,
	},
	{
		path: '/forgot-password-confirm',
		element: <ConfirmPassword />,
	},
	{
		element: <Private />,
		children: [
			{
				path: '/',
				element: <Layout />,
				children: [
					{
						index: true,
						element: <Home />,
					},
					{
						path: '/offline',
						element: <Offline />,
					},
					{
						path: '/categories/:id',
						element: <CategoriesIndividual />,
					},
					{
						path: '/categories',
						element: <Categories />,
					},
					{
						path: '/categories/product-rates',
						element: <Relativity />,
					},
					{
						path: '/bundle-analytics',
						element: <BundleAnalytics />,
					},
					{
						path: '/basket-analytics',
						element: <BasketAnalytics />,
					},
					{
						path: '/users/:id',
						element: <EditUser />,
					},
					{
						path: '/users',
						element: <Users />,
					},
					{
						path: '/gallagher-users',
						element: <GallagherUsers />,
					},
					{
						path: '/policies',
						element: <Policies />,
					},
					{
						path: '/discount-codes',
						element: <DiscountCodes />,
					},
					{
						path: '/arma-karma-marketing',
						element: <AKMarketing />,
					},
					{
						path: '/journey-system',
						element: <JourneySystem />,
					},
					{
						path: '/feefo-sales',
						element: <FeefoSales />,
					},
					{
						path: '/system-admin',
						element: <SystemAdmin />,
					},
					{
						path: '/gis-system',
						element: <GISSystem />,
					},
					{
						path: '/uni-policies',
						element: <UniPolicies />,
					},
					{
						path: '/uni-policies/:id',
						element: <UniPolicyDetails />,
					},
					{
						path: '/referring-domains',
						element: <ReferringDomain />,
					},
					{
						path: '/profile',
						element: <Profile />,
					},
				],
			},
		],
	},
];

export const navigation = [
	{
		name: 'Home',
		path: '/',
		icon: <FontAwesomeIcon icon={['fad', 'pie-chart']} />,
	},
	{
		name: 'GIS System',
		path: '/gis-system',
		icon: <FontAwesomeIcon icon={['fas', 'globe']} />,
	},
	{
		name: 'Feefo sales',
		path: '/feefo-sales',
		icon: <FontAwesomeIcon icon={faThumbsUp} />,
	},
	{
		name: 'Student policies',
		path: '/uni-policies',
		icon: <FontAwesomeIcon icon={faFileContract} />,
	},
];

export default routes;
